import { useState, type FormEvent } from "react";
import { useNavigate } from "@tanstack/react-router";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { useAuth } from "@/features/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";

type Step = "credentials" | "newPassword" | "mfaSetup" | "mfa" | "forgotPassword" | "resetCode";

export function LoginPage() {
  const { login, confirmNewPassword, setupTotp, verifyTotpSetup, confirmMfa, forgotPassword, confirmForgotPassword, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [totpUri, setTotpUri] = useState("");
  const [totpSecret, setTotpSecret] = useState("");
  const [step, setStep] = useState<Step>("credentials");
  const [resetEmail, setResetEmail] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [resetNewPassword, setResetNewPassword] = useState("");
  const [resetConfirmPassword, setResetConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (isAuthenticated) {
    navigate({ to: "/" });
    return null;
  }

  async function handleNextStep(nextStep: string) {
    if (nextStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
      setStep("newPassword");
    } else if (nextStep === "CONTINUE_SIGN_IN_WITH_TOTP_SETUP") {
      setLoading(true);
      try {
        const totp = await setupTotp();
        setTotpUri(totp.totpUri);
        setTotpSecret(totp.secretCode);
        setStep("mfaSetup");
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to set up MFA");
      } finally {
        setLoading(false);
      }
    } else if (nextStep === "CONFIRM_SIGN_IN_WITH_TOTP_CODE") {
      setStep("mfa");
    } else if (nextStep === "DONE") {
      navigate({ to: "/" });
    }
  }

  async function handleLogin(e: FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const result = await login(email, password);
      await handleNextStep(result.nextStep);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Login failed");
    } finally {
      setLoading(false);
    }
  }

  async function handleNewPassword(e: FormEvent) {
    e.preventDefault();
    setError("");
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      const result = await confirmNewPassword(newPassword);
      await handleNextStep(result.nextStep);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to set new password");
    } finally {
      setLoading(false);
    }
  }

  async function handleMfaSetup(e: FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await verifyTotpSetup(mfaCode);
      navigate({ to: "/" });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Invalid code. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  async function handleMfa(e: FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await confirmMfa(mfaCode);
      navigate({ to: "/" });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Invalid MFA code");
    } finally {
      setLoading(false);
    }
  }

  async function handleForgotPassword(e: FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await forgotPassword(resetEmail);
      setStep("resetCode");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to send reset code");
    } finally {
      setLoading(false);
    }
  }

  async function handleResetPassword(e: FormEvent) {
    e.preventDefault();
    setError("");
    if (resetNewPassword !== resetConfirmPassword) {
      setError("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await confirmForgotPassword(resetEmail, resetCode, resetNewPassword);
      toast.success("Password reset successfully. Please sign in.");
      setResetEmail("");
      setResetCode("");
      setResetNewPassword("");
      setResetConfirmPassword("");
      setStep("credentials");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to reset password");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 dark:bg-gray-900">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 inline-block rounded-lg bg-white px-4 py-2">
            <img src="/mdkl-logo.svg" alt="MDKL" className="h-16 w-auto" />
          </div>
          <CardTitle>Link Management</CardTitle>
          <CardDescription>
            {step === "credentials" && "Sign in to your account"}
            {step === "newPassword" && "Please set a new password"}
            {step === "mfaSetup" && "Set up two-factor authentication"}
            {step === "mfa" && "Enter your MFA code"}
            {step === "forgotPassword" && "Reset your password"}
            {step === "resetCode" && "Enter the code sent to your email"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="mb-4 rounded-md bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-300">{error}</div>
          )}

          {step === "credentials" && (
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label htmlFor="email" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Email
                </label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@mdkl.com"
                  required
                  autoComplete="email"
                />
              </div>
              <div>
                <label htmlFor="password" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Password
                </label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                />
              </div>
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? "Signing in..." : "Sign in"}
              </Button>
              <Button
                type="button"
                variant="link"
                className="w-full"
                onClick={() => {
                  setError("");
                  setResetEmail(email);
                  setStep("forgotPassword");
                }}
              >
                Forgot password?
              </Button>
            </form>
          )}

          {step === "newPassword" && (
            <form onSubmit={handleNewPassword} className="space-y-4">
              <div>
                <label htmlFor="newPassword" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  New Password
                </label>
                <Input
                  id="newPassword"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                />
              </div>
              <div>
                <label htmlFor="confirmPassword" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Confirm Password
                </label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                />
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Must be at least 12 characters with uppercase, lowercase, numbers, and symbols.
              </p>
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? "Setting password..." : "Set new password"}
              </Button>
            </form>
          )}

          {step === "mfaSetup" && (
            <form onSubmit={handleMfaSetup} className="space-y-4">
              <div className="text-center">
                <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
                  Scan this QR code with your authenticator app (e.g. Google Authenticator, Authy).
                </p>
                <div className="mx-auto mb-4 inline-block rounded-lg bg-white p-4 shadow-sm">
                  <QRCodeSVG value={totpUri} size={200} />
                </div>
                <details className="mb-4 text-left">
                  <summary className="cursor-pointer text-xs text-gray-500 dark:text-gray-400">
                    Can't scan? Enter this code manually
                  </summary>
                  <code className="mt-2 block break-all rounded bg-gray-100 p-2 text-xs dark:bg-gray-700">
                    {totpSecret}
                  </code>
                </details>
              </div>
              <div>
                <label htmlFor="setupCode" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Verification Code
                </label>
                <Input
                  id="setupCode"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value)}
                  placeholder="000000"
                  required
                  autoComplete="one-time-code"
                />
              </div>
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? "Verifying..." : "Verify & complete setup"}
              </Button>
            </form>
          )}

          {step === "mfa" && (
            <form onSubmit={handleMfa} className="space-y-4">
              <div>
                <label htmlFor="mfa" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Authenticator Code
                </label>
                <Input
                  id="mfa"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value)}
                  placeholder="000000"
                  required
                  autoComplete="one-time-code"
                />
              </div>
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? "Verifying..." : "Verify"}
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={() => {
                  setStep("credentials");
                  setMfaCode("");
                }}
              >
                Back to login
              </Button>
            </form>
          )}
          {step === "forgotPassword" && (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div>
                <label htmlFor="resetEmail" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Email
                </label>
                <Input
                  id="resetEmail"
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  placeholder="you@mdkl.com"
                  required
                  autoComplete="email"
                />
              </div>
              <Button type="submit" className="w-full" disabled={loading || !resetEmail}>
                {loading ? "Sending code..." : "Send Reset Code"}
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={() => {
                  setError("");
                  setStep("credentials");
                }}
              >
                Back to login
              </Button>
            </form>
          )}

          {step === "resetCode" && (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <label htmlFor="resetCode" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Verification Code
                </label>
                <Input
                  id="resetCode"
                  type="text"
                  inputMode="numeric"
                  value={resetCode}
                  onChange={(e) => setResetCode(e.target.value)}
                  placeholder="Enter code from email"
                  required
                  autoComplete="one-time-code"
                />
              </div>
              <div>
                <label htmlFor="resetNewPassword" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  New Password
                </label>
                <Input
                  id="resetNewPassword"
                  type="password"
                  value={resetNewPassword}
                  onChange={(e) => setResetNewPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                />
              </div>
              <div>
                <label htmlFor="resetConfirmPassword" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Confirm Password
                </label>
                <Input
                  id="resetConfirmPassword"
                  type="password"
                  value={resetConfirmPassword}
                  onChange={(e) => setResetConfirmPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                />
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Must be at least 12 characters with uppercase, lowercase, numbers, and symbols.
              </p>
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? "Resetting..." : "Reset Password"}
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={() => {
                  setError("");
                  setStep("credentials");
                }}
              >
                Back to login
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
